import { useResetNodeDataMappings, useSaveNodeDataMappings } from "../../api/nodeApi";
import { NodeConfig } from "../../types/nodes";
import { NodeConnection, NodeDataMapping, WorkflowNode, WorkflowOutput } from "../../types/workflow";
import { getShouldRefetchConfig } from "./utils";

const getNodeDataMappingsFromConfig = (currentWorkflow: WorkflowOutput, nodeConfig: Record<string, string|null>, nodeConnection: NodeConnection) => {      
    const mappings: Partial<NodeDataMapping>[] = Object.entries(nodeConfig || {}).map(([configKey, configValue]) => ({
            id: "",
            workflow: currentWorkflow,
            user_id: currentWorkflow?.user_id,
            start_node_data: configValue || '',
            end_node_data: configKey,
            node_connection: nodeConnection,
    }));
    return mappings;
}

const useDataMappings = (currentWorkflow: WorkflowOutput | undefined, getNodeConfigsForNodeFromRef: (nodeId: string) => NodeConfig) => {
    const { mutate: saveNodeDataMappings } = useSaveNodeDataMappings();
    const { mutate: resetNodeDataMappings } = useResetNodeDataMappings();

    const modifySingleDataMapping = (
        workflowNode: WorkflowNode,
        keyToSave: string,
        refreshSavedRunConfig: (nodeId: string) => void,
        setIsFetchingSavedNodeConfig: (value: boolean) => void
    ) => {
        const nodeId = workflowNode.node.id;
        if (currentWorkflow && nodeId) {
            const { run_config, run_config_metadata } = getNodeConfigsForNodeFromRef(nodeId);
            const refNodeId = run_config_metadata?.[keyToSave]?.ref_node_id ||
                            workflowNode?.nodeConnection?.start_node.id;
            setIsFetchingSavedNodeConfig(true);
            saveNodeDataMappings({
                workflow: currentWorkflow,
                start_node_data: run_config[keyToSave] || '',
                end_node_data: keyToSave,
                node_connection: workflowNode?.nodeConnection!,
                user_id: currentWorkflow?.user_id || '',
                ref_node_id: refNodeId,
            }, {
                onSuccess: () => {
                    const dataMappingsForNode = getNodeDataMappingsFromConfig(currentWorkflow, run_config, workflowNode.nodeConnection!);
                    // const shouldRefetchConfig = getShouldRefetchConfig(workflowNode, dataMappingsForNode);
                    if (typeof refreshSavedRunConfig === 'function') {
                        refreshSavedRunConfig(nodeId);
                    }
                },
                onSettled: () => {
                    setIsFetchingSavedNodeConfig(false);
                }
            });
        }
    }

    const modifyDataMappings = (
        workflowNode: WorkflowNode,
        keyToSave: string,
        refreshSavedRunConfig: (nodeId: string) => void
    ) => {
        const nodeId = workflowNode.node.id;
        if (currentWorkflow && nodeId) {
            const { run_config, run_config_metadata } = getNodeConfigsForNodeFromRef(nodeId);

            const payload: {
                nodeId: string,
                workflowId: string,
                runConfig: { key: string; ref_node_id: string; value: string; }[]
            } = {
                nodeId: nodeId,
                workflowId: currentWorkflow.id,
                runConfig: [],
            }
            Object.entries(run_config || {}).forEach(([key, value]) => {
                const refNodeId = run_config_metadata?.[key]?.ref_node_id || workflowNode?.nodeConnection?.start_node.id;

                payload.runConfig.push({
                    key: key,
                    ref_node_id: refNodeId || '',
                    value: value || '',
                });
            });

            resetNodeDataMappings(payload, {
                onSuccess: () => {
                    if (typeof refreshSavedRunConfig === 'function') {
                        refreshSavedRunConfig(nodeId);
                    }
                }
            });
        }
    }

    return {
        modifySingleDataMapping,
        modifyDataMappings,
        getNodeDataMappingsFromConfig
    };
};

export default useDataMappings;